/**
 * Auto-Raid Protocol (M3 S3a) — runs the L1 loop hands-off.
 *
 * The raid itself is already timer-driven; this automates the button clicks
 * around it. One cycle is: generate grid → chart a path (Auto-Scout) → staff it
 * (Foreman's Ledger) → RE-TOOL → launch → wait → collect → repeat.
 *
 * TICK-DRIVEN, NOT AN ASYNC CHAIN. Each tick advances at most one step and
 * decides what to do purely from `gameState`, so a reload mid-raid resumes
 * correctly, a player taking manual control is noticed on the next tick, and
 * there is no long-lived promise holding a stale view of the world. The cost is
 * that this reads as a state machine rather than a script; that is the point.
 *
 * WHY THE RE-TOOL STEP IS NOT OPTIONAL (docs/layers/3-syndicate/design.md §4.1):
 * gear coverage is not "one device per skill". Whether a synergy variant fires
 * depends on the pairing between the player's tooled technique and what the
 * hired crew brings, so a kit that synergised with the last crew may synergise
 * with none of this one's. A player does this by hand off the `swap` prompt.
 * Automating the raid WITHOUT automating the swap would make automation a
 * downgrade in loot quality — the upgrade would actively cost the player value.
 * Same argument for artifact slots, which are otherwise frozen at whatever was
 * crafted first.
 */

import { gameState, saveGameState } from './gameState.js';
import { addNotification } from './notifications.js';
import { getUpgradeRank } from './command-core.js';
import { swapGearForSynergy } from './contractor-core.js';
import { reequipArtifacts } from './gear-core.js';
import { calculateEnergyCost } from './raid-core.js';
import { isSuccessionArmed, successionStatus } from './syndicate-core.js';
import { debug, CH } from './debugLog.js';
import { activeBlackout } from './blackout-core.js';

/** Minimum team HP fraction to launch another raid. Below this, stop and heal. */
export const MIN_TEAM_HP_FRACTION = 0.5;

/** Guards against two cycles overlapping while an await is in flight. */
let stepInFlight = false;

export function isAutoRaidOwned() {
    return getUpgradeRank('auto-raid-protocol') > 0;
}

export function isAutoRaidEnabled() {
    return isAutoRaidOwned() && gameState.settings?.autoRaidEnabled === true;
}

export function setAutoRaidEnabled(enabled) {
    if (!gameState.settings) gameState.settings = {};
    gameState.settings.autoRaidEnabled = !!enabled;
    saveGameState();
    return gameState.settings.autoRaidEnabled;
}

/**
 * Why the cycle cannot continue right now, or null when it can.
 *
 * Separate from the tick so the UI can explain itself: a toggle that silently
 * does nothing is worse than no toggle. Returns a reason code plus copy.
 */
export function autoRaidBlockedReason() {
    const character = gameState.character;
    if (!character) return { code: 'no-character', text: 'No active character' };
    if (character.isAlive === false) return { code: 'character-down', text: 'Your character is down' };
    if (character.isMarketplace || character.marketplaceData) {
        return { code: 'listed', text: 'Character is listed on the marketplace' };
    }
    const raid = gameState.currentRaid || {};
    if (raid.raidOwnerId && raid.raidOwnerId !== gameState.activeCharacterId) {
        return { code: 'not-owner', text: 'Another character owns the current raid' };
    }
    // Blackouts are DELIBERATE challenge content: the whole point is a player
    // choosing to run under hard modifiers. Automation clearing one would pay a
    // permanent perk for a decision nobody made — and Lights Out disables the
    // Auto-Scout this loop depends on, so it could not run it anyway.
    if (activeBlackout()) {
        return { code: 'blackout-armed', text: 'Blackout contract armed — challenge raids are run by hand' };
    }
    if (!raid.selectedRegion || !raid.selectedTier) {
        // Deliberately NOT auto-picked. Region and tier are the player's
        // standing orders — the automation executes them, it does not choose
        // them, and silently raiding somewhere unintended is worse than idling.
        return { code: 'no-orders', text: 'Pick a region and tier — automation runs those orders' };
    }

    const team = raidTeam();
    const hpFraction = teamHpFraction(team);
    if (hpFraction < MIN_TEAM_HP_FRACTION) {
        return { code: 'low-hp', text: `Team at ${Math.round(hpFraction * 100)}% HP — resting` };
    }
    return null;
}

function raidTeam() {
    const hired = gameState.currentRaid?.hiredContractors || [];
    return [gameState.character, ...hired].filter(Boolean);
}

function teamHpFraction(team) {
    const max = team.reduce((sum, m) => sum + (m.maxHp || 0), 0);
    if (max <= 0) return 1;
    return team.reduce((sum, m) => sum + (m.currentHp || 0), 0) / max;
}

/** Energy needed for the planned path, or null when there is no path yet. */
export function plannedEnergyCost() {
    const raid = gameState.currentRaid || {};
    if (!raid.selectedPath || raid.selectedPath.length < 2 || !raid.selectedTier) return null;
    return calculateEnergyCost(raid.selectedTier, raid.selectedPath);
}

/**
 * Stop automation and tell the player why.
 *
 * Sticky on purpose: automation stopping is the one thing an idle player must
 * not miss — the whole value of the upgrade is not having to watch it, so a
 * message that fades in 5s would strand them doing nothing for hours.
 */
function haltWith(message) {
    setAutoRaidEnabled(false);
    addNotification(`🤖 Auto-Raid stopped — ${message}`, 'warning', { sticky: true });
}

/**
 * Is the cycle armed but stalled on energy regenerating?
 *
 * Not a blocked reason — it resolves on its own — but it IS the longest idle
 * window in an automated run, which makes it the moment Auto-Fabricator should
 * be working. Exported so the status line and that decision read the same
 * condition rather than two copies that can drift.
 */
export function isWaitingForEnergy() {
    if (!isAutoRaidEnabled() || autoRaidBlockedReason()) return false;
    const cost = plannedEnergyCost();
    return cost != null && (gameState.player?.energy || 0) < cost;
}

/**
 * Advance the automation by at most one step. Safe to call every tick.
 *
 * Ordered by raid lifecycle, most-advanced first, so the common case (a raid is
 * running; do nothing) costs one check.
 */
export async function autoRaidTick(api) {
    if (!isAutoRaidEnabled() || stepInFlight) return;

    const raid = gameState.currentRaid || {};

    // A raid is running: the existing timers own it. Nothing to do.
    if (raid.raidInProgress) return;

    // A finished raid is waiting to be collected.
    if (raid.raidCompleted) {
        stepInFlight = true;
        try {
            api.resetRaid();
            saveGameState();
            debug(CH.raid, 'Auto-Raid: collected completed raid');

            // Succession Doctrine rides on the same moment: the rate was just
            // re-evaluated at raid end, and promotion needs the raid cleared
            // (canPromoteCore blocks on raid state), which resetRaid just did.
            if (isSuccessionArmed() && successionStatus().due && api.promoteAndSucceed) {
                api.promoteAndSucceed(successionStatus());
            }
        } finally { stepInFlight = false; }
        return;
    }

    const blocked = autoRaidBlockedReason();
    if (blocked) {
        // Transient states (mid-heal, no character yet) should pause rather than
        // switch the feature off — only genuinely player-resolvable states halt.
        if (blocked.code === 'low-hp') return;
        haltWith(blocked.text);
        return;
    }

    stepInFlight = true;
    try {
        // 1. Grid.
        if (!raid.currentRaidGrid) {
            api.generateGrid();
            debug(CH.raid, 'Auto-Raid: generated grid');
            return;
        }

        // 2. Path.
        if (!raid.selectedPath || raid.selectedPath.length < 2) {
            api.autoScoutPath();
            if (!gameState.currentRaid.selectedPath || gameState.currentRaid.selectedPath.length < 2) {
                haltWith('could not chart a route');
            }
            return;
        }

        // 3. Energy, checked once the path exists because the cost depends on it.
        const cost = plannedEnergyCost();
        if (cost != null && (gameState.player.energy || 0) < cost) {
            // Not a halt: energy regenerates, so this is the normal idle state
            // between raids and the cycle resumes on its own.
            return;
        }

        // 4. Crew.
        if ((raid.reservedContractors || []).length < 2 && (gameState.contractors || []).length > 0) {
            await api.autoReserveContractors();
            // Deliberately no halt on failure — raiding solo is worse than
            // raiding with one contractor, but both beat not raiding at all.
            return;
        }

        // 5. Re-tool for THIS crew, then launch. These two run in the same step
        //    as the launch so the kit can never be one raid stale.
        retoolForRaid();
        // `automated` selects the headless auto-grant presenters for this raid:
        // nobody is watching to click a 💎 or a 🐟, so the collectible would
        // expire and take its loot with it. Passed as an argument rather than
        // read from state inside raid.js on purpose — raid.js importing this
        // module closes a cycle (raid -> autoRaid -> gameState -> raid) that
        // silently breaks boot.
        await api.startRaid({ automated: true });
        debug(CH.raid, 'Auto-Raid: launched');
    } finally {
        stepInFlight = false;
    }
}

/**
 * Re-tool the character for the crew and path about to run.
 * Exported so the tests can assert it independently of the tick sequencing.
 */
export function retoolForRaid() {
    const character = gameState.character;
    if (!character) return;
    const path = gameState.currentRaid?.selectedPath || [];
    const crew = gameState.currentRaid?.reservedContractors?.length
        ? gameState.currentRaid.reservedContractors
        : (gameState.currentRaid?.hiredContractors || []);

    swapGearForSynergy(character, path, crew);
    reequipArtifacts(character);
}
